/**
 * Opening Razorpay Checkout.
 *
 * The backend has already created the provider order and handed us its id; all
 * this does is show the shopper the gateway. Crucially, none of what comes back
 * from here is treated as proof of payment - the order is confirmed by the
 * signed webhook the provider sends our server, and the handler below only
 * decides which message the shopper sees next.
 *
 * That is why there is no signature verification in this file. Verifying a
 * signature in the browser would prove nothing: anything the page can compute,
 * a shopper can fake.
 */

const CHECKOUT_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

const BRAND_MARK = '/images/logo.png';

/**
 * The NeonCarts mark, as an absolute URL.
 *
 * Checkout renders on Razorpay's own origin, so a root-relative path would
 * resolve against theirs and 404 - leaving the initial-in-a-square placeholder
 * the shopper sees when no image is given. Built from the running origin rather
 * than hardcoded so dev, preview and production each serve their own copy.
 *
 * Only for the screens NeonCarts itself is selling on: a storefront's Checkout
 * is the merchant's shop, and must not wear our logo.
 */
export function neonCartsLogoUrl(): string {
  return new URL(BRAND_MARK, window.location.origin).href;
}

interface RazorpayOptions {
  key: string;
  /** Paise. Required for an order; Checkout reads it from the plan for a subscription. */
  amount?: number;
  currency?: string;
  name: string;
  description?: string;
  /** Absolute URL of the logo shown in Checkout's header, beside `name`. */
  image?: string;
  /** One of the two: a one-off order, or a subscription to authorise. */
  order_id?: string;
  subscription_id?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  handler: (response: RazorpayHandlerResponse | RazorpaySubscriptionHandlerResponse) => void;
  modal?: { ondismiss?: () => void };
}

export interface RazorpayHandlerResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

/** What Checkout hands back after a mandate is authorised. */
export interface RazorpaySubscriptionHandlerResponse {
  razorpay_payment_id: string;
  razorpay_subscription_id: string;
  razorpay_signature: string;
}

interface RazorpayConstructor {
  new (options: RazorpayOptions): { open: () => void };
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

/** The payload `POST /cart/checkout` returns for an online payment. */
export interface RazorpayPaymentPayload {
  razorpay_order_id: string;
  key_id: string;
}

export function isRazorpayPayload(value: unknown): value is RazorpayPaymentPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.razorpay_order_id === 'string' && typeof candidate.key_id === 'string'
  );
}

/** The payload `PUT /subscription` returns for an auto-renewing plan. */
export interface RazorpaySubscriptionPayload {
  razorpay_subscription_id: string;
  key_id: string;
}

export function isRazorpaySubscriptionPayload(
  value: unknown,
): value is RazorpaySubscriptionPayload {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.razorpay_subscription_id === 'string' &&
    typeof candidate.key_id === 'string'
  );
}

let scriptPromise: Promise<void> | null = null;

/**
 * Load the checkout script once, no matter how many times we are called.
 *
 * A shopper who dismisses the modal and tries again must not download it twice,
 * and two concurrent calls must not race to append two tags.
 */
export function loadRazorpayScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No browser'));
  if (window.Razorpay) return Promise.resolve();

  scriptPromise ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = CHECKOUT_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever -
      // this is usually a flaky network, not a permanent condition.
      scriptPromise = null;
      reject(new Error('Could not reach the payment gateway.'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export interface OpenCheckoutArgs {
  payment: RazorpayPaymentPayload;
  /** Rupees, as the decimal string the API returned. */
  amount: string;
  currency?: string;
  storeName: string;
  orderNumber: string;
  /**
   * The line under the store's name in Checkout. Omitted reads "Order <number>",
   * which is what every order payment has always shown; a booking or a
   * session package names itself instead.
   */
  description?: string;
  customer: { name: string; email: string; phone?: string };
  /** Absolute URL of the logo for Checkout's header. Omitted leaves the initial. */
  logoUrl?: string;
  themeColor?: string;
  onPaid: (response: RazorpayHandlerResponse) => void;
  onDismissed: () => void;
}

export async function openRazorpayCheckout(args: OpenCheckoutArgs): Promise<void> {
  await loadRazorpayScript();

  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Could not reach the payment gateway.');

  new Razorpay({
    key: args.payment.key_id,
    // Paise, and rounded rather than truncated: 1799.99 must become 179999,
    // not 179998, which floating-point multiplication alone would risk.
    amount: Math.round(Number(args.amount) * 100),
    currency: args.currency ?? 'INR',
    name: args.storeName,
    description: args.description ?? `Order ${args.orderNumber}`,
    ...(args.logoUrl ? { image: args.logoUrl } : {}),
    order_id: args.payment.razorpay_order_id,
    prefill: {
      name: args.customer.name,
      email: args.customer.email,
      contact: args.customer.phone,
    },
    notes: { order_number: args.orderNumber },
    ...(args.themeColor ? { theme: { color: args.themeColor } } : {}),
    handler: (response) => args.onPaid(response as RazorpayHandlerResponse),
    modal: { ondismiss: args.onDismissed },
  }).open();
}

export interface OpenSubscriptionCheckoutArgs {
  payment: RazorpaySubscriptionPayload;
  name: string;
  description: string;
  customer: { name: string; email: string; phone?: string };
  /** Absolute URL of the logo for Checkout's header. Defaults to the NeonCarts mark. */
  logoUrl?: string;
  themeColor?: string;
  /**
   * Checkout says the mandate was authorised. Not proof of anything: the
   * server verifies the signature and asks Razorpay, and the webhook remains
   * the source of truth. This only decides what the page says next.
   */
  onAuthorised: (response: RazorpaySubscriptionHandlerResponse) => void;
  onDismissed: () => void;
}

/**
 * Open Checkout to authorise a Razorpay Subscription.
 *
 * No amount is passed: Checkout takes it from the Razorpay plan the
 * subscription was created against, which the server chose from the plan the
 * merchant picked. Nothing about money originates in the browser.
 *
 * The logo defaults to ours because every mandate Checkout authorises here is
 * for a NeonCarts plan - unlike an order, which may be a merchant's own shop.
 */
export async function openRazorpaySubscriptionCheckout(
  args: OpenSubscriptionCheckoutArgs,
): Promise<void> {
  await loadRazorpayScript();

  const Razorpay = window.Razorpay;
  if (!Razorpay) throw new Error('Could not reach the payment gateway.');

  new Razorpay({
    key: args.payment.key_id,
    subscription_id: args.payment.razorpay_subscription_id,
    name: args.name,
    description: args.description,
    image: args.logoUrl ?? neonCartsLogoUrl(),
    prefill: {
      name: args.customer.name,
      email: args.customer.email,
      contact: args.customer.phone,
    },
    ...(args.themeColor ? { theme: { color: args.themeColor } } : {}),
    handler: (response) => args.onAuthorised(response as RazorpaySubscriptionHandlerResponse),
    modal: { ondismiss: args.onDismissed },
  }).open();
}
